import { useWorkspaceText } from '../interface-copy';
import { useCurrentConnectionRequest } from '../connect-access/use-current-request';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { customMcpApi, type CustomMcpAuthType, type CustomMcpTransport } from './api';
import { authLabels, errorMessage } from './presentation';
import { useCompany } from '@/company/context';
import type { ConnectionAccessChoice } from '../connection-read-access/api';
import { ConnectionPolicyFields, useConnectionPeople } from '../connect-access/policy-fields';
type HeaderRow = { name: string; value: string };

export function CustomMcpDialog({
  canConnectCompany,
  initial,
  onClose,
  onCreated,
}: {
  canConnectCompany: boolean;
  /** A name and address to start from (an outside agent's setup link); never a sign-in. */
  initial?: { name?: string; url?: string };
  onClose: () => void;
  onCreated: (
    response: Awaited<ReturnType<typeof customMcpApi.create>>,
    popup: Window | null,
    /** The connect-time reading choice, applied only to this new server's own grant. */
    choice: ConnectionAccessChoice,
  ) => void;
}) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  const company = useCompany().data;
  const people = useConnectionPeople();
  const [choice, setChoice] = useState<ConnectionAccessChoice>({
    audience: 'private',
    people: [],
    changes: 'ask',
  });
  const [name, setName] = useState(initial?.name ?? '');
  const [url, setUrl] = useState(initial?.url ?? '');
  const [transport, setTransport] = useState<CustomMcpTransport>('auto');
  const [authType, setAuthType] = useState<CustomMcpAuthType>('none');
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const request = useCurrentConnectionRequest();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [clientIssuer, setClientIssuer] = useState('');
  const [headers, setHeaders] = useState<HeaderRow[]>([{ name: '', value: '' }]);
  const [bodyScrolled, setBodyScrolled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialFocus = useRef<HTMLInputElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialog.current?.showModal();
    initialFocus.current?.focus();
  }, []);
  const headerRecord = useMemo(
    () =>
      Object.fromEntries(
        headers.filter((row) => row.name.trim()).map((row) => [row.name.trim(), row.value]),
      ),
    [headers],
  );
  function changeAuthType(value: CustomMcpAuthType) {
    setAuthType(value);
    setToken('');
    setUsername('');
    setPassword('');
    setClientId('');
    setClientSecret('');
    setClientIssuer('');
    setHeaders([{ name: '', value: '' }]);
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!name.trim() || !url.trim()) {
      setError(uiText('Add a name and a web address.'));
      return;
    }
    try {
      new URL(url);
    } catch {
      setError(uiText('Enter a valid web address.'));
      return;
    }
    if (!url.startsWith('https://')) {
      setError(uiText('Use a web address that starts with https://.'));
      return;
    }
    if (authType === 'oauth' && clientId.trim() && !clientIssuer.trim()) {
      setError(uiText('Enter the authorization server issuer when using a client ID.'));
      return;
    }
    const owner = request.begin();
    if (!owner) return;
    const oauthPopup =
      authType === 'oauth' ? window.open('', '_blank', 'width=680,height=760') : null;
    setBusy(true);
    try {
      const auth =
        authType === 'bearer'
          ? { type: authType, ...(token ? { token } : {}) }
          : authType === 'headers'
            ? {
                type: authType,
                ...(Object.keys(headerRecord).length ? { headers: headerRecord } : {}),
              }
            : authType === 'basic'
              ? {
                  type: authType,
                  ...(username ? { username } : {}),
                  ...(password ? { password } : {}),
                }
              : authType === 'oauth'
                ? {
                    type: authType,
                    ...(clientId ? { clientId } : {}),
                    ...(clientSecret ? { clientSecret } : {}),
                    ...(clientIssuer ? { clientIssuer } : {}),
                  }
                : { type: authType };
      const response = await customMcpApi.create(
        {
          name: name.trim(),
          url: url.trim(),
          transport,
          audience: choice.audience === 'company' ? 'company' : 'private',
          auth,
        },
        owner.fetch,
      );
      if (!owner.current()) {
        oauthPopup?.close();
        return;
      }
      setToken('');
      setPassword('');
      setClientSecret('');
      if (oauthPopup && !response.authorizationUrl) oauthPopup.close();
      onCreated(response, oauthPopup, choice);
    } catch (requestError) {
      oauthPopup?.close();
      if (!owner.current()) return;
      setError(errorMessage(requestError, locale));
    } finally {
      if (owner.finish()) setBusy(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      className="ws-custom-mcp-overlay"
      aria-labelledby="custom-mcp-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      onMouseDown={(event) => {
        if (!busy && event.target === event.currentTarget) onClose();
      }}
    >
      <div className="ws-custom-mcp-dialog">
        <div className="ws-custom-mcp-dialog-heading" data-scrolled={bodyScrolled}>
          <div>
            <h2 id="custom-mcp-dialog-title">{uiText('Add a custom connection')}</h2>
            <p>
              {uiText(
                'Connect a tool server you trust. Poneva checks the address works and saves only the connection details needed to use it.',
              )}
            </p>
          </div>
          <button
            type="button"
            className="ws-custom-mcp-close"
            aria-label={uiText('Close')}
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <form onSubmit={submit}>
          <div
            className="ws-custom-mcp-form-body"
            onScroll={(event) => setBodyScrolled(event.currentTarget.scrollTop > 0)}
          >
            <label>
              {uiRich('Name {value1}', {
                value1: (
                  <input
                    ref={initialFocus}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    onInput={(event) => setName(event.currentTarget.value)}
                    placeholder={uiText('e.g. Product analytics')}
                  />
                ),
              })}
            </label>
            <label>
              {uiText('Web address')}
              <input
                type="url"
                inputMode="url"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                onInput={(event) => setUrl(event.currentTarget.value)}
                placeholder={uiText('https://tools.example.com')}
              />
              <small>{uiText('Starts with https://, from your provider')}</small>
            </label>
            <label>
              {uiText('Sign-in')}
              <select
                data-testid="custom-mcp-auth"
                value={authType}
                onInput={(event) => changeAuthType(event.currentTarget.value as CustomMcpAuthType)}
                onChange={(event) => changeAuthType(event.target.value as CustomMcpAuthType)}
              >
                {(['none', 'oauth', 'bearer'] as const).map((value) => (
                  <option key={value} value={value}>
                    {authLabels(locale)[value]}
                  </option>
                ))}
                <optgroup label={uiText('Advanced sign-in')}>
                  {(['headers', 'basic'] as const).map((value) => (
                    <option key={value} value={value}>
                      {authLabels(locale)[value]}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>
            {authType === 'bearer' && (
              <label>
                {uiRich('Access key {value1}', {
                  value1: (
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={token}
                      onChange={(event) => setToken(event.target.value)}
                      onInput={(event) => setToken(event.currentTarget.value)}
                      placeholder={uiText('Paste access key')}
                    />
                  ),
                })}
              </label>
            )}
            {authType === 'headers' && (
              <div className="ws-custom-mcp-headers">
                <div className="ws-custom-mcp-subheading">
                  <strong>{uiText('Request headers')}</strong>
                  <button
                    type="button"
                    className="ws-text-button"
                    onClick={() => setHeaders((current) => [...current, { name: '', value: '' }])}
                  >
                    {uiText('Add header')}
                  </button>
                </div>
                {headers.map((row, index) => (
                  <div className="ws-custom-mcp-header-row" key={index}>
                    <input
                      aria-label={uiText('Header {value1} name', { value1: index + 1 })}
                      value={row.name}
                      onChange={(event) =>
                        setHeaders((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, name: event.target.value } : item,
                          ),
                        )
                      }
                      placeholder={uiText('Header name')}
                    />
                    <input
                      type="password"
                      autoComplete="new-password"
                      aria-label={uiText('Header {value1} value', { value1: index + 1 })}
                      value={row.value}
                      onChange={(event) =>
                        setHeaders((current) =>
                          current.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, value: event.target.value } : item,
                          ),
                        )
                      }
                      placeholder={uiText('Header value')}
                    />
                    {headers.length > 1 && (
                      <button
                        type="button"
                        className="ws-custom-mcp-remove-header"
                        aria-label={uiText('Remove header')}
                        onClick={() =>
                          setHeaders((current) =>
                            current.filter((_, itemIndex) => itemIndex !== index),
                          )
                        }
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {authType === 'basic' && (
              <div className="ws-custom-mcp-form-grid">
                <label>
                  {uiRich('Username {value1}', {
                    value1: (
                      <input
                        value={username}
                        onChange={(event) => setUsername(event.target.value)}
                      />
                    ),
                  })}
                </label>
                <label>
                  {uiRich('Password {value1}', {
                    value1: (
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                    ),
                  })}
                </label>
              </div>
            )}
            <details className="ws-custom-mcp-advanced">
              <summary>{uiText('Advanced')}</summary>
              <label>
                {uiText('Transport')}
                <select
                  value={transport}
                  onChange={(event) => setTransport(event.target.value as CustomMcpTransport)}
                >
                  <option value="auto">{uiText('Auto-detect')}</option>
                  <option value="streamable_http">{uiText('Streamable HTTP')}</option>
                  <option value="sse">{uiText('Legacy SSE')}</option>
                </select>
              </label>
              {authType === 'oauth' && (
                <>
                  <p>
                    {uiText(
                      "From your provider's app settings. Leave empty unless they gave you these.",
                    )}
                  </p>
                  <div className="ws-custom-mcp-form-grid">
                    <label>
                      {uiRich('Client ID {value1}', {
                        value1: (
                          <input
                            name="oauthClientId"
                            type="text"
                            value={clientId}
                            onChange={(event) => setClientId(event.target.value)}
                            onInput={(event) => setClientId(event.currentTarget.value)}
                          />
                        ),
                      })}
                    </label>
                    <label>
                      {uiText('Authorization server issuer')}
                      <input
                        name="oauthClientIssuer"
                        type="url"
                        value={clientIssuer}
                        onChange={(event) => setClientIssuer(event.target.value)}
                        onInput={(event) => setClientIssuer(event.currentTarget.value)}
                      />
                    </label>
                    <label>
                      {uiRich('Client secret {value1}', {
                        value1: (
                          <input
                            type="password"
                            autoComplete="new-password"
                            name="oauthClientSecret"
                            value={clientSecret}
                            onChange={(event) => setClientSecret(event.target.value)}
                            onInput={(event) => setClientSecret(event.currentTarget.value)}
                          />
                        ),
                      })}
                    </label>
                  </div>
                </>
              )}
            </details>
            <ConnectionPolicyFields
              value={choice}
              onChange={setChoice}
              companyName={company?.company?.name ?? ''}
              canCompany={canConnectCompany}
              ownerUserId={company?.membership?.userId ?? null}
              people={people}
              disabled={busy}
            />
            {error && (
              <p className="ws-custom-mcp-form-error" role="alert">
                {error}
              </p>
            )}
          </div>
          <div className="ws-custom-mcp-dialog-actions">
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
              {uiText('Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={busy || (choice.audience === 'people' && !choice.people.length)}
            >
              {busy ? uiText('Connecting…') : uiText('Test and connect')}
            </Button>
          </div>
        </form>
      </div>
    </dialog>
  );
}
